// Components==============
import retrieveUser from 'actions/authentication/retrieve';
import Layout from 'components/Layout/Layout';
import Avatar from 'components/UI/Avatar/Avatar';
import BigToggle from 'components/UI/BigToggle/BigToggle';
import Container from 'components/UI/Container/Container';
import Title from 'components/UI/Title/Title';
import { AnimatePresence, motion } from 'framer-motion';
import { useMediaQ } from 'hooks/useMediaQ';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import { useTranslate } from 'hooks/useTranslate';
import React, { createContext, useEffect, useState } from 'react';
import { framerFade, framerMountScale } from 'utilities/FramerAnimations';
import Colors from './components/Colors/Colors';
import Dreams from './components/Dreams/Dreams';
import Overview from './components/Overview/Overview';
import People from './components/People/People';
import Progress from './components/Progress/Progress';
import Skills from './components/Skills/Skills';
import './GrowthPortfolio.module.scss';
// =========================

export type GrowthPortfolioContextType = {
  loading: boolean;
  selected: SelectedTypes;
  setSelected: React.Dispatch<React.SetStateAction<SelectedTypes>>;
};

export const GrowthPortfolioContext = createContext(
  {} as GrowthPortfolioContextType
);

type SelectedTypes =
  | 'reflection.terms.growth-overview'
  | 'reflection.terms.progress'
  | 'dream.terms.dreams'
  | 'reflection.terms.color-profile'
  | 'reflection.terms.inspiration'
  | 'general.terms.skills';

export default function GrowthPortfolio() {
  const [selected, setSelected] = useState<SelectedTypes>(
    'reflection.terms.growth-overview'
  );

  const dispatch = useReduxAction();

  useEffect(() => {
    dispatch(retrieveUser());
  }, []);

  const translate = useTranslate();
  // @ts-ignore
  const query = useMediaQ('min', 1200);

  const loading = !!useRedux((state) => state.training?.loading);

  const contextValues = {
    loading,
    selected,
    setSelected,
  };

  // also change dreams in dreams component if you change this one
  const dreamsLength =
    useRedux((state) =>
      state.dream?.entities.allIds
        .map((id) => state.dream?.entities.byId[id])
        .filter((d) => !d?.is_public)
    )?.length || 0;

  const smallQuery = useMediaQ('min', 768);
  const bigQuery = useMediaQ('min', 1325);

  const displayDreamGrid =
    dreamsLength > 11
      ? true
      : dreamsLength > 5 && dreamsLength <= 11 && !bigQuery
      ? true
      : !!(dreamsLength <= 5 && !smallQuery);

  const showAvatar =
    bigQuery ||
    (!bigQuery &&
      (selected === 'reflection.terms.color-profile' ||
        selected === 'reflection.terms.growth-overview' ||
        (selected === 'dream.terms.dreams' && !displayDreamGrid)));

  return (
    <Layout grey>
      <GrowthPortfolioContext.Provider value={{ ...contextValues }}>
        <Container marginBottom="1rem">
          <Title
            text={selected}
            loading={loading}
            right={
              <BigToggle
                values={[
                  'reflection.terms.growth-overview',
                  'dream.terms.dreams',
                  'reflection.terms.color-profile',
                  'reflection.terms.inspiration',
                  'general.terms.skills',
                  'reflection.terms.progress',
                ]}
                selected={selected}
                setSelected={setSelected}
                white
                query={query}
              />
            }
          />
          {selected !== 'reflection.terms.progress' && (
            <div
              styleName={`wrapper ${
                selected === 'reflection.terms.color-profile'
                  ? 'colors'
                  : selected === 'general.terms.skills'
                  ? 'skills'
                  : selected === 'reflection.terms.inspiration'
                  ? 'people'
                  : !displayDreamGrid
                  ? 'dream-should-be-centered'
                  : ''
              }`}
            >
              {showAvatar && (
                <motion.div
                  {...framerMountScale}
                  styleName="avatar"
                  onClick={() =>
                    setSelected('reflection.terms.growth-overview')
                  }
                >
                  <Avatar size="huge" border />
                </motion.div>
              )}
              <AnimatePresence>
                {selected === 'reflection.terms.inspiration' ? (
                  <People key={bigQuery ? 1 : undefined} />
                ) : selected === 'dream.terms.dreams' ? (
                  <Dreams
                    key={bigQuery ? 2 : undefined}
                    displayDreamGrid={displayDreamGrid}
                  />
                ) : selected === 'reflection.terms.growth-overview' ? (
                  <Overview key={bigQuery ? 3 : undefined} />
                ) : selected === 'general.terms.skills' ? (
                  <Skills key={bigQuery ? 4 : undefined} />
                ) : selected === 'reflection.terms.color-profile' ? (
                  <Colors key={bigQuery ? 5 : undefined} />
                ) : (
                  <></>
                )}
              </AnimatePresence>
            </div>
          )}
          {selected === 'reflection.terms.progress' && (
            <>
              {query ? (
                <Progress />
              ) : (
                <div styleName="no-mobile">
                  <img
                    src="/images/error-illustrations/thinking-white.svg"
                    alt="query"
                  />
                  <h2>{translate('nocontent.errors.query')}</h2>
                </div>
              )}
            </>
          )}
          <AnimatePresence>
            {!!(
              selected === 'general.terms.skills' ||
              selected === 'reflection.terms.inspiration' ||
              selected === 'reflection.terms.color-profile'
            ) && (
              <motion.p {...framerFade} styleName="description">
                {translate(
                  `reflection.${
                    selected === 'reflection.terms.inspiration'
                      ? 'person'
                      : selected === 'reflection.terms.color-profile'
                      ? 'color-profile'
                      : 'skills'
                  }-description`
                )}
              </motion.p>
            )}
          </AnimatePresence>
        </Container>
      </GrowthPortfolioContext.Provider>
    </Layout>
  );
}
